// Generated by scripts/sync-work-review-contracts.mjs. Do not edit by hand.
import { z } from 'zod';

const uuid = z.string().uuid();
const instant = z.string().datetime({ offset: true });
const serviceId = uuid.nullable().optional();

/** Actor identity is supplied only by authenticated server context. */
export const workExecutionCommandSchema = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('start'),
      workOrderId: uuid,
      bookingServiceId: serviceId,
      description: z.string().max(2000).optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('pause'),
      workOrderId: uuid,
      pauseLocation: z.literal('lift'),
      reason: z.string().max(2000).optional(),
      expectedResumeAt: instant.nullable(),
    })
    .strict(),
  z
    .object({ action: z.literal('resume'), workOrderId: uuid, bookingServiceId: serviceId })
    .strict(),
]);

const timerSchema = z
  .object({
    id: uuid,
    workOrderId: uuid,
    bookingServiceId: uuid.nullable(),
    startedAt: instant,
    description: z.string().nullable(),
    laborRateCents: z.number().finite().nullable(),
    breakHours: z.string(),
  })
  .strict();
const serviceSchema = z
  .object({ id: uuid, status: z.string(), assignedMechanicId: uuid.nullable() })
  .strict();
export const workExecutionStateSchema = z
  .object({
    workOrderId: uuid,
    workOrderOpenTimerId: uuid.nullable(),
    status: z.string(),
    assignedMechanicId: uuid.nullable(),
    pauseLocation: z.string().nullable(),
    expectedResumeAt: instant.nullable(),
    services: z.array(serviceSchema),
    callerOpenTimer: timerSchema.nullable(),
    /**
     * The tenant's IANA zone as staff-api resolves it (`tenantTimezone`), on
     * the GET read only (#3479 round 3): the staff chat resolves a
     * workshop-local own-time entry with it. Absent in command outcomes.
     */
    workshopTimezone: z.string().min(1).optional(),
  })
  .strict();
export const workExecutionEffectsSchema = z
  .object({
    workOrderId: uuid,
    statusBefore: z.string(),
    statusAfter: z.enum(['in_progress', 'paused']),
    assignedMechanicIdBefore: uuid.nullable(),
    assignedMechanicIdAfter: uuid.nullable(),
    assignedToCallerBefore: z.boolean(),
    assignedToCallerAfter: z.boolean(),
    otherOpenTimer: z
      .object({ entryId: uuid, retained: z.literal(true) })
      .strict()
      .nullable(),
    services: z.array(
      z.object({ id: uuid, statusBefore: z.string(), statusAfter: z.string() }).strict()
    ),
    timer: z
      .object({
        effect: z.enum(['open', 'retain', 'close', 'none']),
        entryId: uuid.nullable(),
        bookingServiceId: uuid.nullable(),
        description: z.string().nullable(),
        laborRateCents: z.number().finite().nullable(),
      })
      .strict(),
    pauseLocationBefore: z.string().nullable(),
    pauseReasonBefore: z.string().nullable(),
    expectedResumeAtBefore: instant.nullable(),
    pauseLocationAfter: z.literal('lift').nullable(),
    pauseReasonAfter: z.string().nullable(),
    expectedResumeAtAfter: instant.nullable(),
  })
  .strict();
export const workExecutionReviewSchema = z
  .object({
    command: workExecutionCommandSchema,
    revision: z.string().regex(/^[a-f0-9]{64}$/),
    effects: workExecutionEffectsSchema,
  })
  .strict();
export const reviewedWorkExecutionSchema = z
  .object({
    command: workExecutionCommandSchema,
    review: workExecutionReviewSchema,
  })
  .strict();
export const workExecutionOutcomeSchema = z.discriminatedUnion('outcome', [
  z
    .object({
      outcome: z.literal('applied'),
      state: workExecutionStateSchema,
      alreadyOpen: z.boolean(),
      queue: z.enum(['not_required', 'applied', 'failed']),
    })
    .strict(),
  z.object({ outcome: z.literal('stale'), code: z.string(), message: z.string() }).strict(),
  z
    .object({
      outcome: z.literal('refused'),
      code: z.string(),
      message: z.string(),
      activeWorkOrderId: uuid.optional(),
    })
    .strict(),
  z.object({ outcome: z.literal('unknown'), code: z.string(), message: z.string() }).strict(),
]);
export type WorkExecutionCommand = z.infer<typeof workExecutionCommandSchema>;
export type WorkExecutionReview = z.infer<typeof workExecutionReviewSchema>;
export type WorkExecutionState = z.infer<typeof workExecutionStateSchema>;
export type WorkExecutionEffects = z.infer<typeof workExecutionEffectsSchema>;
export type WorkExecutionOutcome = z.infer<typeof workExecutionOutcomeSchema>;

/** Compare only validated command fields; independent of object key order. */
export function workExecutionCommandsEqual(
  left: WorkExecutionCommand,
  right: WorkExecutionCommand
): boolean {
  if (left.workOrderId !== right.workOrderId || left.action !== right.action) return false;
  if (left.action === 'pause' && right.action === 'pause')
    return left.reason === right.reason && left.expectedResumeAt === right.expectedResumeAt;
  if (left.action === 'start' && right.action === 'start')
    return (
      left.bookingServiceId === right.bookingServiceId && left.description === right.description
    );
  if (left.action === 'resume' && right.action === 'resume')
    return left.bookingServiceId === right.bookingServiceId;
  return false;
}
